import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button, Input } from '@design-system';
import {
  FilterDraft,
  IllustrationMonitorFiltersPanel,
} from '@/features/illustration-monitor/components/illustration-monitor-filters';
import { QuoteCard } from '@/features/illustration-monitor/components/quote-card';
import {
  useMonitorQuoteById,
  useMonitorQuotes,
  useMonitorRun,
  useMonitorRunStatus,
} from '@/features/illustration-monitor/hooks/use-illustration-monitor';
import { MonitorQuote, MonitorQuotesRequest } from '@/features/illustration-monitor/types';

const PAGE_SIZE = 25;

const EMPTY_DRAFT: FilterDraft = {
  insuranceCarrierId: null,
  product: null,
  clientState: null,
  ratedState: null,
  primaryAge: null,
  primaryGender: null,
  primaryTobaccoStatus: null,
  paymentOption: null,
};

export function IllustrationMonitorPage() {
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<MonitorQuotesRequest | null>(null);
  const [page, setPage] = useState(0);
  const [activeRequestByQuote, setActiveRequestByQuote] = useState<Record<string, string>>({});

  const [quoteIdInput, setQuoteIdInput] = useState('');
  const [searchedQuoteId, setSearchedQuoteId] = useState<string | null>(null);
  const quoteByIdQuery = useMonitorQuoteById(searchedQuoteId);

  const canApply = !!(
    draft.insuranceCarrierId &&
    draft.product &&
    draft.clientState &&
    draft.ratedState &&
    draft.primaryAge &&
    draft.primaryGender
  );

  const request: MonitorQuotesRequest | null = applied
    ? { ...applied, limit: PAGE_SIZE, offset: page * PAGE_SIZE }
    : null;

  const quotesQuery = useMonitorQuotes(request);
  const runMutation = useMonitorRun();

  const handleApply = () => {
    if (!canApply) return;
    setSearchedQuoteId(null);
    setApplied({
      insuranceCarrierId: draft.insuranceCarrierId!,
      productNames: draft.product!.variants,
      quoteType: draft.product!.quoteType,
      clientState: draft.clientState!,
      ratedState: draft.ratedState!,
      primaryAge: draft.primaryAge!,
      primaryGender: draft.primaryGender!,
      primaryTobaccoStatus: draft.primaryTobaccoStatus ?? undefined,
      paymentOption: draft.paymentOption ?? undefined,
    });
    setPage(0);
  };

  const handleClear = () => {
    setDraft(EMPTY_DRAFT);
    setApplied(null);
    setSearchedQuoteId(null);
    setQuoteIdInput('');
    setPage(0);
  };

  const handleQuoteIdSearch = () => {
    const trimmed = quoteIdInput.trim();
    if (!trimmed) return;
    setApplied(null);
    setSearchedQuoteId(trimmed);
  };

  const handleGenerate = (quoteId: string, quoteType: string) => {
    runMutation.mutate(
      { quoteId, quoteType, clientState: applied?.clientState ?? undefined },
      {
        onSuccess: ({ requestId }) => {
          setActiveRequestByQuote(prev => ({ ...prev, [quoteId]: requestId }));
        },
      },
    );
  };

  const quotes = quotesQuery.data?.quotes ?? [];
  const total = quotesQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const singleQuote = quoteByIdQuery.data?.quote ?? null;

  return (
    <div className="flex h-screen w-full">
      <IllustrationMonitorFiltersPanel
        value={draft}
        onChange={setDraft}
        onApply={handleApply}
        onClear={handleClear}
        canApply={canApply}
      />

      <main className="flex-1 overflow-auto">
        <div className="border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Illustration Finder</h1>
            <div className="flex items-center gap-2">
              <Button asChild variant="default" size="sm">
                <Link to="/wizard/illustration-finder/logs">Illustration Logs</Link>
              </Button>
              <Button asChild variant="default" size="sm">
                <Link to="/wizard/illustration-finder/availability">
                  State Availability Matrix
                </Link>
              </Button>
              {applied && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => quotesQuery.refetch()}
                  disabled={quotesQuery.isFetching}
                >
                  {quotesQuery.isFetching ? 'Refreshing...' : 'Refresh'}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Look up by quote ID..."
                value={quoteIdInput}
                onChange={e => setQuoteIdInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleQuoteIdSearch()}
                className="pl-9"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleQuoteIdSearch}>
              Search
            </Button>
          </div>
        </div>

        <div className="px-8 py-6">
          {searchedQuoteId && (
            <>
              {quoteByIdQuery.isLoading && (
                <p className="py-8 text-center text-sm text-gray-400">
                  Searching for {searchedQuoteId}...
                </p>
              )}
              {quoteByIdQuery.isSuccess && !singleQuote && (
                <p className="py-8 text-center text-sm text-gray-400">
                  No quote found for ID "{searchedQuoteId}".
                </p>
              )}
              {singleQuote && (
                <>
                  <div className="mb-2 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    <span className="font-medium">{singleQuote.carrierName}</span>
                    {' / '}
                    <span className="font-medium">{singleQuote.productName}</span>
                    {' — '}
                    Rated state: {singleQuote.ratedState}
                    {singleQuote.quoteType === 'traditionalLTC' && (
                      <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600">
                        Traditional LTC
                      </span>
                    )}
                  </div>
                  <div className="mb-4 text-xs text-gray-500">1 quote (direct lookup)</div>
                  <QuoteRow
                    quote={singleQuote}
                    requestId={activeRequestByQuote[singleQuote.quoteId] ?? null}
                    onGenerate={handleGenerate}
                    isGenerating={
                      runMutation.isPending &&
                      runMutation.variables?.quoteId === singleQuote.quoteId
                    }
                  />
                </>
              )}
            </>
          )}

          {!searchedQuoteId && !applied && (
            <p className="py-16 text-center text-sm text-gray-400">
              Select filters and click Load quotes, or search by quote ID.
            </p>
          )}

          {!searchedQuoteId && applied && quotesQuery.isLoading && (
            <p className="py-16 text-center text-sm text-gray-400">Loading quotes...</p>
          )}

          {!searchedQuoteId && applied && quotesQuery.isError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Failed to load quotes.
            </div>
          )}

          {!searchedQuoteId && applied && quotesQuery.isSuccess && quotes.length === 0 && (
            <p className="py-16 text-center text-sm text-gray-400">
              No quotes match. Try adjusting age or tobacco status.
            </p>
          )}

          {!searchedQuoteId && applied && total > 0 && (
            <>
              <div className="mb-2 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <span className="font-medium">
                  {quotesQuery.data?.product?.carrierName}
                </span>
                {' / '}
                <span className="font-medium">{draft.product?.displayName}</span>
                {' — '}
                {applied.clientState}
                {quotesQuery.data?.ratedState && (
                  <span className="text-gray-500"> (rated: {quotesQuery.data.ratedState})</span>
                )}
                {' — '}
                Age {applied.primaryAge}, {applied.primaryGender}
                {applied.primaryTobaccoStatus && `, ${applied.primaryTobaccoStatus}`}
                {applied.quoteType === 'traditionalLTC' && (
                  <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600">
                    Traditional LTC
                  </span>
                )}
              </div>
              <div className="mb-4 text-xs text-gray-500">
                {total} quote{total === 1 ? '' : 's'}
                {' · '}
                showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)}
              </div>

              <div>
                {quotes.map(q => (
                  <QuoteRow
                    key={q.quoteId}
                    quote={q}
                    requestId={activeRequestByQuote[q.quoteId] ?? null}
                    onGenerate={handleGenerate}
                    isGenerating={
                      runMutation.isPending && runMutation.variables?.quoteId === q.quoteId
                    }
                  />
                ))}
              </div>

              {total > PAGE_SIZE && (
                <div className="mt-6 flex items-center justify-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0 || quotesQuery.isFetching}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page + 1 >= totalPages || quotesQuery.isFetching}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function QuoteRow({
  quote,
  requestId,
  onGenerate,
  isGenerating,
}: {
  quote: MonitorQuote;
  requestId: string | null;
  onGenerate: (quoteId: string, quoteType: string) => void;
  isGenerating: boolean;
}) {
  const statusQuery = useMonitorRunStatus(requestId);
  return (
    <QuoteCard
      quote={quote}
      onGenerate={onGenerate}
      isGenerating={isGenerating}
      status={statusQuery.data ?? null}
    />
  );
}
